<template>
  <div class="space-y-4">
    <h2 class="text-center text-lg font-medium text-[var(--plugin-charcoal)]">
      {{ t('plugin.branchesStep.title') }}
    </h2>
    <p v-if="loading" class="text-sm text-[var(--plugin-gray-500)]">{{ t('plugin.loading') }}</p>
    <p v-else-if="error" class="text-sm text-red-600">{{ error }}</p>
    <ul v-else class="flex flex-col gap-3">
      <li
        v-for="b in branches"
        :key="b.id"
        class="cursor-pointer"
        @click="select(b)"
      >
        <div
          class="flex items-center gap-4 rounded-[8px] border bg-card py-4 pl-4 pr-4 transition"
          :class="
            state.branch?.id === b.id
              ? 'border border-primary'
              : 'border-[var(--plugin-border)] hover:border-[var(--plugin-gray-400)]'
          "
        >
          <div class="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-[var(--plugin-gray-100)]">
            <img
              v-if="b.media?.[0]?.url && !imageFailed(b.id)"
              :src="b.media[0].url"
              :alt="b.name"
              class="h-full w-full object-cover"
              @error="onImageError(b.id)"
            />
            <svg
              v-if="!b.media?.[0]?.url || imageFailed(b.id)"
              class="h-6 w-6 text-[var(--plugin-gray-400)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
              />
            </svg>
          </div>
          <div class="min-w-0 flex-1 text-left">
            <div class="text-base font-medium text-[var(--plugin-charcoal)]">
              {{ b.name }}
            </div>
            <div v-if="b.address" class="mt-1.5 text-sm font-normal text-[var(--plugin-gray-400)]">
              {{ b.address }}
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { getBranches } from '@/api/public'
import type { PublicBranch } from '@/types/api'
import { useWizardState, setBranch, setWizardStep } from '@/composables/useWizardState'
import { usePluginParams } from '@/composables/usePluginParams'
import { t } from '@/i18n'

const props = defineProps<{ facilityId: number }>()
const state = useWizardState()
const params = usePluginParams()
const branches = ref<PublicBranch[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const imageLoadFailedIds = ref<Set<number>>(new Set())

function imageFailed(branchId: number) {
  return imageLoadFailedIds.value.has(branchId)
}

function onImageError(branchId: number) {
  imageLoadFailedIds.value = new Set([...imageLoadFailedIds.value, branchId])
}

function select(b: PublicBranch) {
  setBranch(b)
}

watch(
  () => props.facilityId,
  async (id) => {
    if (!id) return
    loading.value = true
    error.value = null
    imageLoadFailedIds.value = new Set()
    try {
      branches.value = await getBranches(id)
      const hasUrlPreselection = !!(params.value.branchId ?? params.value.serviceId)
      const noBranchSelectedYet = state.branch == null
      if (branches.value.length === 1 && !hasUrlPreselection && noBranchSelectedYet) {
        setBranch(branches.value[0])
        setWizardStep('products')
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('plugin.error.load')
    } finally {
      loading.value = false
    }
  },
  { immediate: true }
)
</script>
